import moment from "moment";

import type {
  Actions as ListActions,
  FilterAppointmentType,
  State as AppointmentListState,
} from "react-lib/apps/MEH/sequence/AppointmentListMEH";
import {
  getDayOfWeekThai,
  getDayOfWeekThaiLong,
} from "react-lib/apps/MEH/appointments/AppointmentMeHeyUtils";
import { formatDate, formatThaiMonth } from "react-lib/utils/dateUtils";

import type { ChipV4Tone } from "../common/ui";

/* ============================================================
   domain ล้วนของหน้า "นัดหมาย" (ไม่ import SDK)
   ข้อมูลมาจาก sequence V3: AppointmentListMEH (รายชื่อ) และ MakeAppointmentMeHey (ปฏิทิน)
   ============================================================ */

// ---------- sequence ----------

export const LIST_SEQUENCE = "AppointmentListMEH" as const;
export const MAKE_SEQUENCE = "MakeAppointmentMeHey" as const;

export const LIST_ACTIONS: ListActions = {
  SEARCH: "SEARCH",
  UPDATE_APPOINTMENT: "UPDATE_APPOINTMENT",
  SELECTED_APPOINTMENT_DETAIL: "SELECTED_APPOINTMENT_DETAIL",
  ASSIGN_APPOINTMENT_TO_BLOCK: "ASSIGN_APPOINTMENT_TO_BLOCK",
  CONFIRM_APPOINTMENT: "CONFIRM_APPOINTMENT",
  SET_APPOINTMENT_FINISH: "SET_APPOINTMENT_FINISH",
  CONFIRM_APPOINTMENT_FINISH: "CONFIRM_APPOINTMENT_FINISH",
  GET_PACKAGE_DETAIL: "GET_PACKAGE_DETAIL",
};

export const MAKE_ACTIONS = {
  SET_PROVIDER_FILTER: "SET_PROVIDER_FILTER",
  SET_CURRENT_DATE: "SET_CURRENT_DATE",
  SET_VIEW_TYPE: "SET_VIEW_TYPE",
  SET_SHOW_CREATE_MODAL: "SET_SHOW_CREATE_MODAL",
  BATCH_DL: "BATCH_DL",
  SAVE_APPOINTMENT: "SAVE_APPOINTMENT",
  CLOSE_ERROR_MODAL: "CLOSE_ERROR_MODAL",
} as const;

/** key ของ buttonLoadCheck (= BTN_ACTS ของ AppointmentListMEH) */
export const LIST_BTN = {
  SEARCH: `${LIST_SEQUENCE}_SEARCH`,
  CONFIRM_APPOINTMENT: `${LIST_SEQUENCE}_CONFIRM_APPOINTMENT`,
  CONFIRM_APPOINTMENT_FINISH: `${LIST_SEQUENCE}_CONFIRM_APPOINTMENT_FINISH`,
} as const;

/** key ของ errorMessage ที่ AppointmentListMEH เขียน */
export const LIST_ERROR_KEY = LIST_SEQUENCE;

/** ค้นหาผู้ป่วยผ่าน event ItemSearch (key ใน searchedItemListWithKey = `${type}_${id}`) */
export const PATIENT_FILTER_SEARCH = { type: "OfficialAccountPatientList", id: "AL" } as const;
export const PATIENT_CREATE_SEARCH = {
  type: "ProxyPatientHasDivisionAdminList",
  id: "AMH",
} as const;

export const searchKeyOf = (search: { type: string; id: string }): string =>
  `${search.type}_${search.id}`;

export type AppointmentListSlice = NonNullable<AppointmentListState["AppointmentListMEHSequence"]>;

export type ApiRecord = Record<string, any>;

export type ProviderRecord = {
  provider_id: number;
  pre_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

export type ViewType = "month" | "week" | "day";

export type MakeAppointmentSlice = Partial<{
  sequenceIndex: string;
  allProviders: ProviderRecord[];
  dsb: ApiRecord[] | null;
  selectedProviderIdsList: number[];
  currentDate: Date | string;
  viewType: ViewType;
  showSuccess: boolean;
  showError: boolean;
  errorMessage: unknown;
  showCreateModal: boolean;
  AppointmentLoading: unknown;
  selectedAppointmentDetail: ApiRecord | null;
}>;

// ---------- ตัวกรองรายชื่อ ----------

export const ALL = "ทั้งหมด";

export type Option = { value: string; text: string };

export const emptyListFilter = (): FilterAppointmentType => ({
  id: "",
  patientID: "",
  startDate: "",
  endDate: "",
  providerID: ALL,
  appointmentStatus: ALL,
  orderStatus: ALL,
  noDivisionServiceBlock: false,
});

/** ค่า status ของ API ตาม appointmentStatusOptions */
export const APPOINTMENT_STATUS_OPTIONS: Option[] = [
  { value: ALL, text: ALL },
  { value: "2,8", text: "ยืนยันนัดหมาย" },
  { value: "1", text: "รอยืนยัน" },
  { value: "5,6", text: "ยกเลิก" },
];

export const SERVICE_STATUS_OPTIONS: Option[] = [
  { value: ALL, text: ALL },
  { value: "1,2,3,4,5,6,7", text: "ยังไม่ได้รับบริการ" },
  { value: "8", text: "รับบริการแล้ว" },
];

export const providerName = (provider: ProviderRecord): string =>
  [provider.pre_name, provider.first_name, provider.last_name].filter(Boolean).join(" ").trim();

export const providerOptions = (
  providers: ProviderRecord[] | undefined,
  withAll: boolean,
): Option[] => [
  ...(withAll ? [{ value: ALL, text: ALL }] : []),
  ...(providers ?? []).map((provider) => ({
    value: String(provider.provider_id),
    text: providerName(provider) || String(provider.provider_id),
  })),
];

/** ชื่อผู้ป่วยจากผลค้นหา (pre_name first_name last_name) */
export const personName = (item: ApiRecord): string =>
  [item.pre_name, item.first_name, item.last_name].filter(Boolean).join(" ").trim();

/** HN ของผลค้นหาผู้ป่วย (proxy-patient-has-division: hn ชั้นนอกว่าง, อยู่ใน patient_detail) */
export const hnOf = (item: ApiRecord): string => String(item.hn || item.patient_detail?.hn || "");

// ---------- วันที่ ----------

const DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/** วันที่ที่พิมพ์ วว/ดด/ปปปป (พ.ศ. หรือ ค.ศ.) -> moment (ค.ศ.) หรือ null ถ้าไม่ถูกต้อง */
export const parseDateInput = (text: string): moment.Moment | null => {
  const match = DATE_RE.exec(text.trim());
  if (!match) return null;
  const year = Number(match[3]);
  const adYear = year > 2400 ? year - 543 : year;
  const value = moment(
    `${adYear}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`,
    "YYYY-MM-DD",
    true,
  );
  return value.isValid() ? value : null;
};

/** ข้อความในช่องวันที่ -> DD/MM/YYYY (พ.ศ.) สำหรับ filter; "" = ว่าง, null = ไม่ถูกต้อง */
export const toBeDateText = (text: string): string | null => {
  if (!text.trim()) return "";
  const value = parseDateInput(text);
  return value ? formatDate(value) : null;
};

export const dateKeyOf = (date: Date | moment.Moment): string => moment(date).format("YYYY-MM-DD");

export const toDate = (value: Date | string | undefined | null): Date => {
  const parsed = moment(value ?? undefined);
  return parsed.isValid() ? parsed.toDate() : moment().toDate();
};

/** "พุธ 21/10/2569" */
export const panelDateText = (date: Date): string =>
  `${getDayOfWeekThaiLong(moment(date).day())} ${formatDate(date)}`;

export const DAY_HEAD = [0, 1, 2, 3, 4, 5, 6].map((day) => getDayOfWeekThai(day));

const thaiYear = (date: Date): number => moment(date).year() + 543;

/** ชื่อช่วงบนแถบควบคุม */
export const rangeTitle = (view: ViewType, anchor: Date): string => {
  if (view === "month") return `${formatThaiMonth(anchor)} ${thaiYear(anchor)}`;
  if (view === "week") {
    const [first, last] = [weekCells(anchor)[0], weekCells(anchor)[6]];
    return `${moment(first).date()} ${formatThaiMonth(first)} – ${moment(last).date()} ${formatThaiMonth(
      last,
    )} ${thaiYear(last)}`;
  }
  return `${getDayOfWeekThaiLong(moment(anchor).day())} ${moment(anchor).date()} ${formatThaiMonth(
    anchor,
  )} ${thaiYear(anchor)}`;
};

/** วันแรก + จำนวนวันต่อกัน */
const daysFrom = (start: moment.Moment, count: number): Date[] =>
  Array.from({ length: count }, (_, index) => start.clone().add(index, "days").toDate());

/** ช่องวันของเดือน 42 ช่อง เริ่มวันอาทิตย์ */
export const monthCells = (anchor: Date): Date[] =>
  daysFrom(moment(anchor).startOf("month").day(0), 42);

/** 7 วันของสัปดาห์ (อาทิตย์-เสาร์) ที่มี anchor */
export const weekCells = (anchor: Date): Date[] =>
  daysFrom(moment(anchor).startOf("day").day(0), 7);

export const cellsOf = (view: ViewType, anchor: Date): Date[] =>
  view === "month"
    ? monthCells(anchor)
    : view === "week"
      ? weekCells(anchor)
      : daysFrom(moment(anchor).startOf("day"), 1);

/** ‹ › : เดือน = วันที่ 1 ของเดือนก่อน/ถัดไป, สัปดาห์ ±7, วัน ±1 */
export const shiftAnchor = (view: ViewType, anchor: Date, step: 1 | -1): Date => {
  if (view === "month") return moment(anchor).add(step, "months").startOf("month").toDate();

  return moment(anchor)
    .startOf("day")
    .add(view === "week" ? 7 * step : step, "days")
    .toDate();
};

export const sameDay = (a: Date, b: Date): boolean => dateKeyOf(a) === dateKeyOf(b);

export const isPastDay = (date: Date | moment.Moment, today: Date = new Date()): boolean =>
  dateKeyOf(date) < dateKeyOf(today);

// ---------- แถวของตารางรายชื่อ ----------

export type RowAction = "confirm" | "finish" | "postpone" | "cancel";

export const ROW_ACTION_TEXT: Record<RowAction, string> = {
  confirm: "ยืนยันนัดหมาย",
  finish: "รับบริการแล้ว",
  postpone: "เลื่อนแบบไม่ระบุวัน",
  cancel: "ยกเลิกนัด",
};

/** type ที่ CONFIRM_APPOINTMENT รับ */
export const CONFIRM_TYPE = {
  confirm: "ยืนยันนัดหมาย",
  finish: "ได้รับบริการแล้ว",
  postpone: "เลื่อนแบบไม่ระบุวัน",
} as const;

export const CANCEL_TYPES = ["ผู้รับบริการขอยกเลิก", "ผู้ให้บริการขอยกเลิก"] as const;

export type CancelType = (typeof CANCEL_TYPES)[number];

export const POSTPONED_TEXT = "ไม่ระบุวัน (เลื่อน)";

export type ListRow = {
  id: number | string;
  name: string;
  hn: string;
  tel: string;
  when: string;
  insertQueue: boolean;
  provider: string;
  statusText: string;
  statusTone: ChipV4Tone;
  serviceText: string;
  serviceTone: ChipV4Tone;
  serviceDate: string;
  actions: RowAction[];
  raw: ApiRecord;
};

const CANCELLED = "ยกเลิกนัดหมาย";
const PENDING_STATUS = [1, 4];
const CONFIRMED = "ยืนยันนัดหมาย";
const SERVED = "รับบริการแล้ว";

/** ป้ายสถานะนัด: ข้อความของ sequence V3, ยกเลิก แสดงเป็น "ยกเลิก" ตาม design */
export const appointmentChip = (text: string): { text: string; tone: ChipV4Tone } => {
  if (text === CANCELLED) return { text: "ยกเลิก", tone: "grey" };
  if (text === CONFIRMED) return { text, tone: "ok" };
  return { text: text || "-", tone: "amber" };
};

export const serviceChip = (text: string): { text: string; tone: ChipV4Tone } =>
  text === SERVED ? { text, tone: "sky" } : { text: text || "-", tone: "amber" };

export const toListRow = (item: ApiRecord): ListRow => {
  const statusText =
    String(item.appointment_status_text ?? "") ||
    (PENDING_STATUS.includes(Number(item.status)) ? "รอยืนยัน" : "");
  const serviceText = String(item.service_status_text ?? "");
  const cancelled = statusText === CANCELLED;
  const served = serviceText === SERVED;
  const status = appointmentChip(statusText);
  const service = serviceChip(serviceText);

  const dated = !!item.division_service_block || cancelled;
  const actions: RowAction[] = [];
  if (!cancelled && !served) {
    if (PENDING_STATUS.includes(Number(item.status)) && dated) actions.push("confirm");
    actions.push("finish");
    if (dated) actions.push("postpone");
    actions.push("cancel");
  }

  return {
    id: item.id,
    name: [item.patient_first_name, item.patient_last_name].filter(Boolean).join(" ") || "-",
    hn: item.patient_hn ? String(item.patient_hn) : "",
    tel: item.patient_tel || "-",
    when: dated ? item.estimated_at || "-" : POSTPONED_TEXT,
    insertQueue: item.type_code === "APP_INSERT",
    provider: item.doctor_full_name || "-",
    statusText: status.text,
    statusTone: status.tone,
    serviceText: service.text,
    serviceTone: service.tone,
    serviceDate: item.service_date_text || "-",
    actions,
    raw: item,
  };
};

/** แถวที่ส่ง "รับบริการแล้ว" ได้ (ไม่ยกเลิก ไม่รับบริการแล้ว) */
export const canFinish = (row: ListRow): boolean =>
  row.actions.includes("finish") && row.when !== POSTPONED_TEXT;

/** รายการของ AppointmentFinish (รูปเดียวกับ SET_APPOINTMENT_FINISH) */
export const finishItemsOf = (rows: ListRow[], ids: (number | string)[]) =>
  rows
    .filter((row) => ids.includes(row.id) && canFinish(row))
    .map((row) => ({
      checked: true,
      id: row.raw.content_id,
      patientName: row.raw.patient,
      estimatedAt: row.raw.estimated_at,
      doctorName: row.raw.doctor_full_name,
    }));

// ---------- ปฏิทิน (DivisionServiceBlockFilter) ----------

export type BlockItem = {
  id: number | string;
  time: string;
  name: string;
  shortName: string;
  status: number;
  statusText: string;
  statusTone: ChipV4Tone;
  provider: string;
  providerId: number;
  hn: string;
  insertQueue: boolean;
};

export type Block = {
  dsbId: number;
  providerId: number;
  provider: string;
  date: string;
  start: string;
  end: string;
  order: number;
  colorIndex: number;
  full?: boolean;
  items: BlockItem[];
  raw: ApiRecord;
};

/** สถานะนัดใน DSB (ข้อความตาม design ที่มีคู่ใน V3, ที่เหลือใช้ข้อความ V3) */
export const blockStatusChip = (status: number): { text: string; tone: ChipV4Tone } => {
  switch (status) {
    case 1:
    case 4:
      return { text: "รอยืนยัน", tone: "amber" };
    case 2:
      return { text: "ยืนยันแล้ว", tone: "ok" };
    case 3:
      return { text: "ถูกเลื่อนนัด", tone: "amber" };
    case 7:
      return { text: "ไม่มาตามนัด", tone: "grey" };
    case 8:
      return { text: "เสร็จสิ้น", tone: "sky" };
    default:
      return { text: "-", tone: "grey" };
  }
};

const CANCELLED_STATUS = [5, 6];

const hhmm = (time: unknown): string => String(time ?? "").slice(0, 5);

/** "พิมพ์ชนก ด." */
export const shortNameOf = (first: string, last: string): string =>
  [first, last ? `${last.charAt(0)}.` : ""].filter(Boolean).join(" ");

/** dsb[].providers[].dsb -> บล็อกของผู้ให้บริการ; ลำดับและสี (วน 3 สี) ตาม allProviders */
export const blocksFromDsb = (
  dsb: ApiRecord[] | null | undefined,
  providers: ProviderRecord[] | undefined,
): Block[] => {
  const order = (providers ?? []).map((provider) => provider.provider_id);
  const blocks: Block[] = [];

  for (const day of dsb ?? []) {
    for (const providerItem of day?.providers ?? []) {
      const raw = providerItem?.dsb;
      if (!raw?.doctor_dsb_id || !raw.date) continue;
      const providerId = Number(raw.doctor_provider_id);
      const provider = String(raw.doctor_provider_name ?? "");
      const index = order.indexOf(providerId);
      const items: BlockItem[] = [];

      for (const apt of raw.appointments ?? []) {
        const status = Number(apt.status);
        if (CANCELLED_STATUS.includes(status)) continue;
        const chip = blockStatusChip(status);
        const at = moment(apt.estimated_at);
        items.push({
          id: apt.id,
          time: at.isValid() ? at.format("HH:mm") : "",
          name: [apt.first_name, apt.last_name].filter(Boolean).join(" "),
          shortName: shortNameOf(apt.first_name ?? "", apt.last_name ?? ""),
          status,
          statusText: chip.text,
          statusTone: chip.tone,
          provider,
          providerId,
          hn: String(apt.hn ?? ""),
          insertQueue: apt.type_code === "APP_INSERT",
        });
      }

      blocks.push({
        dsbId: Number(raw.doctor_dsb_id),
        providerId,
        provider,
        date: String(raw.date),
        start: hhmm(raw.doctor_start_time),
        end: hhmm(raw.doctor_end_time),
        order: index < 0 ? order.length + providerId : index,
        colorIndex: ((index < 0 ? blocks.length : index) % 3) + 1,
        full: !!raw.doctor_dsb_full,
        items,
        raw,
      });
    }
  }

  return blocks;
};

export const blocksOn = (blocks: Block[], dateKey: string, providerId: number | null): Block[] =>
  blocks
    .filter(
      (block) => block.date === dateKey && (providerId === null || block.providerId === providerId),
    )
    .sort((a, b) => a.order - b.order || a.start.localeCompare(b.start));

export const itemsOf = (blocks: Block[]): BlockItem[] =>
  blocks.flatMap((block) => block.items).sort((a, b) => a.time.localeCompare(b.time));

// ---------- modal สร้างนัด ----------

const toMinutes = (time: string): number => {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
};

const fromMinutes = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** ช่อง 30 นาทีระหว่าง start..end (รวม end แบบ V3); ไม่มีตาราง = 07:00-21:00 */
export const timeSlots = (start = "07:00", end = "21:00"): string[] => {
  const slots: string[] = [];
  for (let minutes = toMinutes(start); minutes <= toMinutes(end); minutes += 30) {
    slots.push(fromMinutes(minutes));
  }
  return slots;
};

export const isTimeText = (text: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(text.trim());

export type CreateForm = {
  providerId: number | null;
  dateText: string;
  manual: boolean;
  time: string;
  patientId: number | string | null;
  patientName: string;
  patientHn?: string;
};

/** ตรวจฟอร์มสร้างนัดตามลำดับของ design เท่าที่มีข้อมูล; คืนข้อความ error หรือ null */
export const validateCreate = (
  form: CreateForm,
  blocks: Block[],
  providerText: string,
  today: Date = new Date(),
): string | null => {
  if (!form.patientId) return "กรุณาเลือกผู้รับบริการ";
  if (form.manual && !isTimeText(form.time)) return "เวลาไม่ถูกต้อง · พิมพ์เป็น HH:MM";
  const date = parseDateInput(form.dateText);
  if (!date) return "วันที่ไม่ถูกต้อง · พิมพ์เป็น วว/ดด/ปปปป (พ.ศ. หรือ ค.ศ.)";
  if (isPastDay(date, today)) return "ไม่สามารถนัดย้อนหลังได้";
  const dayName = getDayOfWeekThaiLong(date.day());
  const block = blockFor(blocks, form.providerId, dateKeyOf(date), form.time);
  if (!block) return `${providerText} ไม่เปิดรับนัดวัน${dayName}`;
  const minutes = toMinutes(form.time.trim());
  if (form.manual && (minutes < toMinutes(block.start) || minutes > toMinutes(block.end))) {
    return `${providerText} เปิดรับนัดวัน${dayName} ${block.start} - ${block.end} เท่านั้น`;
  }
  const time = form.time.trim();
  if (
    form.patientHn &&
    block.items.some((item) => item.hn === form.patientHn && item.time === time)
  ) {
    return "ผู้ป่วยมีนัดเวลานี้กับผู้ให้บริการนี้แล้ว";
  }
  if (block.full && !form.manual) return fullSlotText(time, false);
  return null;
};

/** แถบเตือนเมื่อตารางของผู้ให้บริการเต็ม */
export const fullSlotText = (time: string, manual: boolean): string =>
  `ช่วงเวลา ${time} เต็มแล้ว · ${
    manual ? 'บันทึกได้เป็นนัด "เสริม"' : 'ถ้าจำเป็นต้องแทรก ให้ติ๊ก "กำหนดเวลาแบบ manual"'
  }`;

/** บล็อกของผู้ให้บริการในวันนั้น (หลายบล็อก = อันที่ครอบเวลา) */
export const blockFor = (
  blocks: Block[],
  providerId: number | null,
  dateKey: string,
  time: string,
): Block | null => {
  if (providerId === null) return null;
  const candidates = blocksOn(blocks, dateKey, providerId);
  return (
    candidates.find((block) => time >= block.start && time <= block.end) ?? candidates[0] ?? null
  );
};

/** appointmentData ของ SAVE_APPOINTMENT (date เป็น ค.ศ. DD/MM/YYYY แบบ V3) */
export const saveParamsOf = (form: CreateForm & { details: string }, block: Block) => {
  const date = parseDateInput(form.dateText);
  return {
    appointmentData: {
      doctor: form.providerId,
      date: date ? date.format("DD/MM/YYYY") : "",
      startTime: block.start,
      endTime: block.end,
      appointmentTime: form.time.trim(),
      manualTime: form.manual,
      patientID: form.patientId,
      patientName: form.patientName,
      details: form.details,
    },
    dsb: block.raw,
  };
};
